import React, { useEffect, useMemo, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("id-ID", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function Reports() {
  const [data, setData] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [accounting, setAccounting] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [tab, setTab] = useState("summary");
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => {
    const value = {};
    if (outletId) value.outlet_id = outletId;
    if (start) value.start = start;
    if (end) value.end = end;
    return value;
  }, [outletId, start, end]);

  const load = async () => {
    setLoading(true);
    try {
      const [summaryResponse, ledgerResponse, accountingResponse] = await Promise.all([
        api.get("/reports/summary", { params }),
        api.get("/reports/debit-credit", { params }),
        api.get("/reports/accounting-journal", { params }),
      ]);
      setData(summaryResponse.data);
      setLedger(ledgerResponse.data);
      setAccounting(accountingResponse.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Laporan gagal dimuat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setOutlets((await api.get("/outlets")).data || []);
      } catch {
        setOutlets([]);
      }
    })();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const maxRev = (data?.by_day || []).reduce((m, d) => Math.max(m, d.revenue), 1);

  const exportDebitCredit = () => {
    const rows = ledger?.entries || [];
    if (!rows.length) {
      toast.error("Belum ada data debit/kredit untuk diekspor");
      return;
    }
    const header = ["Tanggal", "Referensi", "Jenis", "Keterangan", "Outlet", "Pihak", "Debit", "Kredit", "Saldo"];
    const lines = [header.map(csvCell).join(",")];
    rows.forEach((row) => {
      lines.push([
        formatDateTime(row.created_at),
        row.reference,
        row.type_label,
        row.description,
        row.outlet_name,
        row.counterparty,
        Number(row.debit || 0),
        Number(row.credit || 0),
        Number(row.balance || 0),
      ].map(csvCell).join(","));
    });
    lines.push("");
    lines.push([csvCell("TOTAL"), "", "", "", "", "", csvCell(ledger.total_debit || 0), csvCell(ledger.total_credit || 0), csvCell(ledger.net_balance || 0)].join(","));
    const blob = new Blob([`\ufeff${lines.join("\r\n")}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `kraya-debit-kredit-${start?.slice(0, 10) || "awal"}-${end?.slice(0, 10) || "sekarang"}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="overline text-zinc-500">ANALITIK</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Laporan</h1>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 border border-border bg-white px-4 py-2 text-sm font-semibold hover:bg-zinc-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <select data-testid="report-outlet" value={outletId} onChange={(e)=>setOutletId(e.target.value)} className="border border-border px-3 py-2 text-sm">
          <option value="">Semua Outlet</option>{outlets.map(o=><option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <input data-testid="report-start" type="date" value={start.slice(0,10)} onChange={(e)=>setStart(e.target.value)} className="border border-border px-3 py-2 text-sm"/>
        <input data-testid="report-end" type="date" value={end.slice(0,10)} onChange={(e)=>setEnd(e.target.value? e.target.value + "T23:59:59" : "")} className="border border-border px-3 py-2 text-sm"/>
      </div>

      <div className="mb-6 inline-flex border border-border bg-white p-1">
        <button
          type="button"
          onClick={() => setTab("summary")}
          className={`px-4 py-2 text-sm font-semibold ${tab === "summary" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
        >
          Ringkasan
        </button>
        <button
          type="button"
          onClick={() => setTab("debit-credit")}
          className={`px-4 py-2 text-sm font-semibold ${tab === "debit-credit" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
        >
          Neraca Debit / Kredit
        </button>
        <button
          type="button"
          onClick={() => setTab("accounting")}
          className={`px-4 py-2 text-sm font-semibold ${tab === "accounting" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
        >
          Jurnal COA
        </button>
      </div>

      {tab === "summary" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 grid-borders mb-8">
            {[
              { l: "Pendapatan", v: formatIDR(data?.revenue||0) },
              { l: "HPP / COGS", v: formatIDR(data?.cogs||0) },
              { l: "Laba Kotor", v: formatIDR((data?.revenue||0)-(data?.cogs||0)), color:"text-emerald-700" },
              { l: "Total Pembelian", v: formatIDR(data?.purchase_total||0) },
              { l: "Transaksi", v: (data?.tx_count||0) },
            ].map((s) => (
              <div key={s.l} className="p-5 bg-white">
                <div className="overline text-zinc-500">{s.l}</div>
                <div className={"mt-2 font-display font-black text-2xl lg:text-3xl tabular-nums " + (s.color||"")} data-testid={`rep-${s.l.toLowerCase().replace(/\s+/g,"-")}`}>{s.v}</div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-0 grid-borders">
            <div className="lg:col-span-2 p-6 bg-white">
              <div className="overline text-zinc-500 mb-4">PENDAPATAN HARIAN</div>
              {data?.by_day?.length ? (
                <div className="space-y-2">
                  {data.by_day.map((d) => (
                    <div key={d.day} className="flex items-center gap-3">
                      <div className="w-24 text-xs font-mono">{d.day}</div>
                      <div className="flex-1 h-6 bg-zinc-100 relative">
                        <div className="absolute inset-y-0 left-0 bg-[#002FA7]" style={{ width: `${(d.revenue/maxRev)*100}%` }} />
                      </div>
                      <div className="w-28 text-right text-xs tabular-nums">{formatIDR(d.revenue)}</div>
                      <div className="w-16 text-right text-xs text-zinc-500 tabular-nums">{d.tx}x</div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-sm text-zinc-500">Tidak ada data</div>}
            </div>
            <div className="p-6 bg-white">
              <div className="overline text-zinc-500 mb-4">METODE BAYAR</div>
              <div className="space-y-3">
                {Object.entries(data?.by_method || {}).map(([k, v]) => (
                  <div key={k}>
                    <div className="flex justify-between text-sm font-semibold uppercase"><span>{k}</span><span className="tabular-nums">{formatIDR(v)}</span></div>
                    <div className="h-2 bg-zinc-100 mt-1"><div className="h-full bg-zinc-900" style={{width: `${(v/(data?.revenue||1))*100}%`}}/></div>
                  </div>
                ))}
              </div>
              <div className="overline text-zinc-500 mt-8 mb-4">TOP PRODUK</div>
              <div className="space-y-2">
                {(data?.top_products||[]).slice(0,5).map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="font-display font-black w-5 text-zinc-400 tabular-nums">{i+1}</div>
                    <div className="flex-1 text-sm truncate">{p.name}</div>
                    <div className="text-xs tabular-nums">{formatIDR(p.revenue)}</div>
                  </div>
                ))}
                {(data?.top_products||[]).length===0 && <div className="text-xs text-zinc-500">—</div>}
              </div>
            </div>
          </div>
        </>
      )}

      {tab === "debit-credit" && (
        <div className="space-y-6">
          <div className="border border-blue-200 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-900">
            <strong>Dasar laporan:</strong> Debit = pembelian yang tercatat, Kredit = penjualan berstatus lunas, dan Saldo Bersih = Kredit - Debit. Laporan ini merupakan neraca transaksi operasional, bukan neraca keuangan formal double-entry.
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 grid-borders">
            {[
              { label: "Total Debit", value: formatIDR(ledger?.total_debit || 0), note: `${ledger?.purchase_count || 0} pembelian`, color: "text-rose-700" },
              { label: "Total Kredit", value: formatIDR(ledger?.total_credit || 0), note: `${ledger?.sales_count || 0} penjualan`, color: "text-emerald-700" },
              { label: "Saldo Bersih", value: formatIDR(ledger?.net_balance || 0), note: "Kredit - Debit", color: Number(ledger?.net_balance || 0) >= 0 ? "text-emerald-700" : "text-rose-700" },
              { label: "Total Transaksi", value: ledger?.transaction_count || 0, note: "Penjualan + pembelian", color: "" },
            ].map((item) => (
              <div key={item.label} className="bg-white p-5">
                <div className="overline text-zinc-500">{item.label}</div>
                <div className={`mt-2 font-display text-2xl font-black tabular-nums lg:text-3xl ${item.color}`}>{item.value}</div>
                <div className="mt-1 text-xs text-zinc-500">{item.note}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-4 border border-border bg-white p-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="overline text-zinc-500">KREDIT PENJUALAN PER METODE</div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm">
                {Object.entries(ledger?.sales_by_method || {}).map(([method, amount]) => (
                  <div key={method} className="font-semibold uppercase">
                    {method}: <span className="tabular-nums">{formatIDR(amount)}</span>
                  </div>
                ))}
                {Object.keys(ledger?.sales_by_method || {}).length === 0 && <span className="text-zinc-500">Belum ada penjualan.</span>}
              </div>
            </div>
            <button
              type="button"
              onClick={exportDebitCredit}
              className="inline-flex items-center justify-center gap-2 border border-zinc-900 bg-zinc-900 px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-800"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>

          <div className="overflow-hidden border border-border bg-white">
            <div className="border-b border-border px-5 py-4">
              <div className="overline text-zinc-500">BUKU TRANSAKSI DEBIT / KREDIT</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-sm">
                <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-4 py-3">Tanggal</th>
                    <th className="px-4 py-3">Referensi</th>
                    <th className="px-4 py-3">Jenis</th>
                    <th className="px-4 py-3">Keterangan</th>
                    <th className="px-4 py-3">Outlet</th>
                    <th className="px-4 py-3 text-right">Debit</th>
                    <th className="px-4 py-3 text-right">Kredit</th>
                    <th className="px-4 py-3 text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {(ledger?.entries || []).map((row) => (
                    <tr key={row.id} className="hover:bg-zinc-50/70">
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-zinc-600">{formatDateTime(row.created_at)}</td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs">{row.reference}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${row.type === "sale" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
                          {row.type_label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{row.description}</div>
                        <div className="text-xs text-zinc-500">{row.counterparty}</div>
                      </td>
                      <td className="px-4 py-3">{row.outlet_name}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-rose-700">{Number(row.debit || 0) > 0 ? formatIDR(row.debit) : "-"}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-700">{Number(row.credit || 0) > 0 ? formatIDR(row.credit) : "-"}</td>
                      <td className={`px-4 py-3 text-right font-mono font-semibold tabular-nums ${Number(row.balance || 0) >= 0 ? "text-zinc-900" : "text-rose-700"}`}>{formatIDR(row.balance || 0)}</td>
                    </tr>
                  ))}
                  {(ledger?.entries || []).length === 0 && (
                    <tr><td colSpan="8" className="px-4 py-10 text-center text-sm text-zinc-500">Tidak ada transaksi penjualan atau pembelian pada periode ini.</td></tr>
                  )}
                </tbody>
                <tfoot className="border-t-2 border-zinc-900 bg-zinc-50 font-bold">
                  <tr>
                    <td colSpan="5" className="px-4 py-3 text-right">TOTAL PERIODE</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-rose-700">{formatIDR(ledger?.total_debit || 0)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-700">{formatIDR(ledger?.total_credit || 0)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{formatIDR(ledger?.net_balance || 0)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}


      {tab === "accounting" && (
        <div className="space-y-6">
          <div className={`border px-4 py-3 text-sm leading-6 ${accounting?.balanced === false ? "border-rose-200 bg-rose-50 text-rose-900" : "border-emerald-200 bg-emerald-50 text-emerald-900"}`}>
            <strong>Jurnal double-entry berbasis COA:</strong> transaksi penjualan dan pembelian dipetakan otomatis ke akun sistem. Total debit dan kredit harus selalu seimbang pada periode yang dipilih.
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 grid-borders">
            {[
              { label: "Total Debit", value: formatIDR(accounting?.total_debit || 0), note: "Jurnal COA periode" },
              { label: "Total Kredit", value: formatIDR(accounting?.total_credit || 0), note: "Jurnal COA periode" },
              { label: "Status Jurnal", value: accounting?.balanced === false ? "TIDAK BALANCE" : "BALANCE", note: accounting?.balanced === false ? "Periksa mapping COA" : "Debit = Kredit", color: accounting?.balanced === false ? "text-rose-700" : "text-emerald-700" },
              { label: "Jumlah Jurnal", value: accounting?.journal_count || 0, note: "Penjualan + pembelian" },
            ].map((item) => (
              <div key={item.label} className="bg-white p-5">
                <div className="overline text-zinc-500">{item.label}</div>
                <div className={`mt-2 font-display text-2xl font-black tabular-nums lg:text-3xl ${item.color || ""}`}>{item.value}</div>
                <div className="mt-1 text-xs text-zinc-500">{item.note}</div>
              </div>
            ))}
          </div>

          <div className="border border-border bg-white">
            <div className="border-b border-border px-5 py-4">
              <div className="overline text-zinc-500">NERACA SALDO</div>
              <div className="mt-1 text-xs text-zinc-500">Saldo dihitung mengikuti saldo normal masing-masing akun COA.</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-sm">
                <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-4 py-3">Kode</th>
                    <th className="px-4 py-3">Akun</th>
                    <th className="px-4 py-3">Tipe</th>
                    <th className="px-4 py-3">Saldo Normal</th>
                    <th className="px-4 py-3 text-right">Debit</th>
                    <th className="px-4 py-3 text-right">Kredit</th>
                    <th className="px-4 py-3 text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {(accounting?.trial_balance || []).map((row) => (
                    <tr key={row.account?.id || row.account?.code} className="hover:bg-zinc-50/70">
                      <td className="px-4 py-3 font-mono font-bold">{row.account?.code || "-"}</td>
                      <td className="px-4 py-3 font-semibold">{row.account?.name || "-"}</td>
                      <td className="px-4 py-3 uppercase text-xs">{row.account?.account_type || "-"}</td>
                      <td className="px-4 py-3 uppercase text-xs font-semibold">{row.account?.normal_balance || "-"}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{Number(row.debit || 0) ? formatIDR(row.debit) : "-"}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{Number(row.credit || 0) ? formatIDR(row.credit) : "-"}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold tabular-nums">{formatIDR(row.balance || 0)}</td>
                    </tr>
                  ))}
                  {(accounting?.trial_balance || []).length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-zinc-500">Belum ada jurnal pada periode ini.</td></tr>}
                </tbody>
                <tfoot className="border-t-2 border-zinc-900 bg-zinc-50 font-bold">
                  <tr>
                    <td colSpan="4" className="px-4 py-3 text-right">TOTAL MUTASI</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{formatIDR(accounting?.total_debit || 0)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{formatIDR(accounting?.total_credit || 0)}</td>
                    <td className="px-4 py-3 text-right">{accounting?.balanced === false ? "TIDAK BALANCE" : "BALANCE"}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="border border-border bg-white">
            <div className="border-b border-border px-5 py-4">
              <div className="overline text-zinc-500">JURNAL OTOMATIS</div>
            </div>
            <div className="divide-y divide-zinc-100">
              {(accounting?.journals || []).map((journal) => (
                <div key={journal.id} className="p-5">
                  <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${journal.source === "sale" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-blue-200 bg-blue-50 text-blue-700"}`}>{journal.source_label}</span>
                        <span className="font-mono text-xs font-bold">{journal.reference}</span>
                        <span className="text-xs text-zinc-500">{journal.outlet_name}</span>
                      </div>
                      <div className="mt-1 text-sm font-semibold">{journal.description}</div>
                    </div>
                    <div className="text-xs text-zinc-500">{formatDateTime(journal.created_at)}</div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[620px] text-sm">
                      <thead className="text-left text-[10px] uppercase tracking-wider text-zinc-400"><tr><th className="py-1">Akun</th><th className="py-1">Nama</th><th className="py-1 text-right">Debit</th><th className="py-1 text-right">Kredit</th></tr></thead>
                      <tbody>
                        {(journal.lines || []).map((line, index) => (
                          <tr key={`${journal.id}-${line.account?.id || index}`} className="border-t border-zinc-100">
                            <td className="py-2 font-mono text-xs font-bold">{line.account?.code || "-"}</td>
                            <td className="py-2">{line.account?.name || "-"}</td>
                            <td className="py-2 text-right font-mono tabular-nums">{Number(line.debit || 0) ? formatIDR(line.debit) : "-"}</td>
                            <td className="py-2 text-right font-mono tabular-nums">{Number(line.credit || 0) ? formatIDR(line.credit) : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="border-t border-zinc-900 font-bold"><tr><td colSpan="2" className="py-2 text-right">TOTAL</td><td className="py-2 text-right font-mono tabular-nums">{formatIDR(journal.total_debit || 0)}</td><td className="py-2 text-right font-mono tabular-nums">{formatIDR(journal.total_credit || 0)}</td></tr></tfoot>
                    </table>
                  </div>
                </div>
              ))}
              {(accounting?.journals || []).length === 0 && <div className="px-5 py-10 text-center text-sm text-zinc-500">Belum ada jurnal penjualan atau pembelian pada periode ini.</div>}
            </div>
          </div>

          <div className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <div className="font-bold">Asumsi posting otomatis</div>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {(accounting?.assumptions || []).map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
